import { API_BASE_URL } from './client'
import { getToken, ensureFreshToken } from './tokenStore'

// --- Backend types (matching backend/src/schemas/chat.py and filters.py) ---

interface BackendFilterConfig {
  jurisdictions: string[]          // jurisdiction name codes (e.g. "CA", "Alberta")
  pub_authorities: string[] | null // pub authority UUIDs, null = all
  legislation_uuids: string[] | null // document legislation UUIDs, null = all docs
}

interface BackendFilterUuids {
  jurisdiction_uuids: string[]
  pub_authority_uuids: string[]
  document_legislation_uuids: string[]
}

interface BackendChatRequest {
  message: string
  conversation_id: number | null
  filters: BackendFilterConfig
  filter_uuids: BackendFilterUuids | null
  stream: boolean
  language: string
}

// --- Citation types (matching backend Citation/CitationSource schemas) ---

export interface Citation {
  cited_text: string
  document_index: number
  document_title: string
  start_char_index: number
  end_char_index: number
  source_index: number  // 1-based index into the deduplicated sources list
  position_fraction: number | null  // 0–1 position of this chunk in the full document
}

export interface CitationSource {
  index: number
  title: string
  url: string | null
  legal_ref: string | null
  legislation_uuid: string | null
  is_pdf: boolean
  viewable: boolean
}

export interface StreamChunk {
  content: string | null
  done: boolean
  message_id: number | null
  conversation_id: number | null
  title: string | null
  error: string | null
  cited_response: string | null
  citations: Citation[] | null
  sources: CitationSource[] | null
}

// --- Frontend types ---

export interface SendMessageRequest {
  message: string
  jurisdictionNames: string[]      // mapped from UUIDs → jurisdiction_name codes
  jurisdictionUuids: string[]      // raw UUIDs for backend persistence
  pubAuthorityUuids: string[]
  documentLegislationUuids: string[]
  conversationId: number | null
  language: string                 // user's interface language code (en, fr, zh)
}

export interface StreamCompleteResult {
  messageId: number
  conversationId: number
  title: string | null
  citedResponse: string | null
  citations: Citation[] | null
  sources: CitationSource[] | null
}

/**
 * Stream from the dev mock regulation endpoint (ENVIRONMENT=dev only).
 * Triggered when the user sends the magic string "__mock_regulation".
 */
export async function* streamMockRegulationMessage(
  signal?: AbortSignal,
): AsyncGenerator<StreamChunk, void, unknown> {
  const response = await fetch(`${API_BASE_URL}/api/v1/dev/mock-chat-regulation`, {
    method: 'POST',
    signal,
  })
  if (!response.ok) throw new Error(`Mock regulation chat failed (${response.status})`)

  const reader = response.body!.getReader()
  const decoder = new TextDecoder()
  let buffer = ''

  try {
    while (true) {
      const { done, value } = await reader.read()
      if (done) break
      buffer += decoder.decode(value, { stream: true })
      const events = buffer.split('\n\n')
      buffer = events.pop() || ''
      for (const event of events) {
        const trimmed = event.trim()
        if (!trimmed.startsWith('data: ')) continue
        try {
          yield JSON.parse(trimmed.slice(6)) as StreamChunk
        } catch { /* ignore */ }
      }
    }
  } finally {
    reader.releaseLock()
  }
}

/**
 * Stream from the dev mock endpoint (ENVIRONMENT=dev only).
 * Triggered when the user sends the magic string "__mock_pdf".
 */
export async function* streamMockChatMessage(
  signal?: AbortSignal,
): AsyncGenerator<StreamChunk, void, unknown> {
  const response = await fetch(`${API_BASE_URL}/api/v1/dev/mock-chat`, {
    method: 'POST',
    signal,
  })
  if (!response.ok) throw new Error(`Mock chat failed (${response.status})`)

  const reader = response.body!.getReader()
  const decoder = new TextDecoder()
  let buffer = ''

  try {
    while (true) {
      const { done, value } = await reader.read()
      if (done) break
      buffer += decoder.decode(value, { stream: true })
      const events = buffer.split('\n\n')
      buffer = events.pop() || ''
      for (const event of events) {
        const trimmed = event.trim()
        if (!trimmed.startsWith('data: ')) continue
        try {
          yield JSON.parse(trimmed.slice(6)) as StreamChunk
        } catch { /* ignore */ }
      }
    }
  } finally {
    reader.releaseLock()
  }
}

/**
 * Stream a chat message from the backend via SSE.
 *
 * Uses fetch() + ReadableStream instead of EventSource because
 * EventSource only supports GET — we need POST with a JSON body.
 *
 * Yields StreamChunk objects parsed from SSE `data: {...}\n\n` lines.
 */
export async function* streamChatMessage(
  request: SendMessageRequest,
  signal?: AbortSignal,
): AsyncGenerator<StreamChunk, void, unknown> {
  const backendRequest: BackendChatRequest = {
    message: request.message,
    conversation_id: request.conversationId,
    filters: {
      jurisdictions: request.jurisdictionNames,
      pub_authorities: request.pubAuthorityUuids.length > 0 ? request.pubAuthorityUuids : null,
      legislation_uuids: request.documentLegislationUuids.length > 0
        ? request.documentLegislationUuids
        : null,
    },
    filter_uuids: {
      jurisdiction_uuids: request.jurisdictionUuids,
      pub_authority_uuids: request.pubAuthorityUuids,
      document_legislation_uuids: request.documentLegislationUuids,
    },
    stream: true,
    language: request.language,
  }

  await ensureFreshToken()

  const response = await fetch(`${API_BASE_URL}/api/v1/chat/`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(getToken() && { 'X-App-Authorization': `Bearer ${getToken()}` }),
    },
    body: JSON.stringify(backendRequest),
    signal,
  })

  if (!response.ok) {
    const errorBody = await response.text()
    throw new Error(`Chat request failed (${response.status}): ${errorBody}`)
  }

  const reader = response.body!.getReader()
  const decoder = new TextDecoder()
  let buffer = ''

  try {
    while (true) {
      const { done, value } = await reader.read()
      if (done) break

      buffer += decoder.decode(value, { stream: true })

      // SSE events are separated by double newlines
      const events = buffer.split('\n\n')
      buffer = events.pop() || '' // keep incomplete segment

      for (const event of events) {
        const trimmed = event.trim()
        if (!trimmed.startsWith('data: ')) continue

        const jsonStr = trimmed.slice(6)
        try {
          const chunk: StreamChunk = JSON.parse(jsonStr)
          yield chunk
        } catch {
          console.warn('Failed to parse SSE chunk:', jsonStr)
        }
      }
    }

    // Process any remaining data in buffer
    const remaining = buffer.trim()
    if (remaining.startsWith('data: ')) {
      try {
        const chunk: StreamChunk = JSON.parse(remaining.slice(6))
        yield chunk
      } catch {
        // ignore trailing parse errors
      }
    }
  } finally {
    reader.releaseLock()
  }
}
